import type { Category, CategoryId, CategoryKind } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { $project, commandIssued, cpiPath, RateOrInheritField } from '../../entities/project';
import { newId } from '../../shared/lib/id';
import { Button, EditOnly, IconButton, Section, TextField, Tooltip } from '../../shared/ui';

/**
 * Categories and their default inflation (item override → category default → global CPI).
 * A category can be deleted only when nothing uses it.
 */
export function CategoriesSection() {
  const { t } = useTranslation();
  const [project, issue] = useUnit([$project, commandIssued]);
  const cpi = useMemo(() => (project ? cpiPath(project) : new Float64Array()), [project]);
  if (!project) return null;

  const used = new Set<string>();
  for (const item of Object.values(project.items)) {
    if (item.type !== 'asset' && item.category) used.add(item.category);
    if (item.type === 'formula')
      for (const s of item.spec.sources) if (s.ref.type === 'category') used.add(s.ref.id);
  }

  const update = (category: Category, field: string): void => {
    issue({
      command: { type: 'category/update', category },
      coalesceKey: `${category.id}:${field}`,
    });
  };
  const add = (kind: CategoryKind): void => {
    issue({
      command: {
        type: 'category/add',
        category: {
          id: newId('cat') as CategoryId,
          name: t(kind === 'income' ? 'categories.newIncome' : 'categories.newExpense'),
          kind,
        },
      },
    });
  };

  return (
    <Section
      title={t('inspector.sections.categories')}
      actions={
        <EditOnly>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              onClick={() => {
                add('income');
              }}
            >
              {t('categories.addIncome')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              onClick={() => {
                add('expense');
              }}
            >
              {t('categories.addExpense')}
            </Button>
          </div>
        </EditOnly>
      }
    >
      <p className="text-xs text-text-3">{t('categories.hint')}</p>
      <ul className="space-y-2">
        {project.categories.map((category) => {
          const inUse = used.has(category.id);
          return (
            <li
              key={category.id}
              className="space-y-1.5 rounded-md border border-border-subtle p-2"
            >
              <div className="flex items-center gap-1.5">
                <span className="w-16 shrink-0 text-2xs text-text-3">
                  {t(`categories.kind.${category.kind}`)}
                </span>
                <TextField
                  className="flex-1"
                  aria-label={t('categories.name')}
                  value={category.name}
                  maxLength={80}
                  onCommit={(name) => {
                    update({ ...category, name }, 'name');
                  }}
                />
                <EditOnly>
                  <Tooltip content={inUse ? t('categories.inUse') : t('categories.delete')}>
                    <span>
                      <IconButton
                        label={`${t('categories.delete')} — ${category.name}`}
                        disabled={inUse}
                        onClick={() => {
                          issue({ command: { type: 'category/remove', id: category.id } });
                        }}
                      >
                        <Trash2 />
                      </IconButton>
                    </span>
                  </Tooltip>
                </EditOnly>
              </div>
              <RateOrInheritField
                label={t('categories.inflation', { name: category.name })}
                value={category.inflation}
                project={project}
                inheritLabel={t('fields.globalCpi')}
                inherited={cpi}
                onCommit={(inflation) => {
                  const { inflation: _, ...rest } = category;
                  update(inflation ? { ...category, inflation } : rest, 'inflation');
                }}
              />
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
